import { create } from 'zustand';
import { useEffect } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'ondeck-theme-preference';

function getStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }
  } catch {
    // localStorage not available
  }
  return 'system';
}

function storeTheme(theme: Theme) {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage not available
  }
}

// Check if dark mode should be active
function shouldUseDarkMode(theme: Theme): boolean {
  if (theme === 'dark') return true;
  if (theme === 'light') return false;
  // System preference
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

// Apply theme to the document
function applyTheme(theme: Theme) {
  const isDark = shouldUseDarkMode(theme);
  const html = document.documentElement;

  // Toggle dark class on html element - Tailwind and our CSS look for this
  if (isDark) {
    html.classList.add('dark');
  } else {
    html.classList.remove('dark');
  }
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isDark: () => boolean;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: getStoredTheme(),

  setTheme: (theme: Theme) => {
    storeTheme(theme);
    applyTheme(theme);
    set({ theme });
  },

  isDark: () => shouldUseDarkMode(get().theme),
}));

// Hook to sync theme on mount and listen for system preference changes
export function useThemeSync() {
  const theme = useThemeStore((state) => state.theme);

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Listen for system preference changes (only affects 'system' theme)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = () => {
      const currentTheme = useThemeStore.getState().theme;
      // ONLY react to system changes if user has chosen 'system'
      if (currentTheme === 'system') {
        applyTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);
}

// Call before React renders to prevent flash (used in main.tsx)
export function initializeTheme() {
  applyTheme(getStoredTheme());
}
